import { Card } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { formatDMY, addDays } from "@/lib/dateUtils";
import { AlertTriangle, CheckCircle2, Clock, CalendarRange, HelpCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { useT } from "@/lib/i18n";

const useStatusStyles = () => {
  const { t } = useT();
  return {
    ok: {
      label: t("status.ok"),
      ring: "border-emerald-500/30",
      dot: "bg-emerald-400",
      text: "text-emerald-300",
      bg: "bg-emerald-500/5",
    },
    warning: {
      label: t("status.warning"),
      ring: "border-amber-500/40",
      dot: "bg-amber-400",
      text: "text-amber-300",
      bg: "bg-amber-500/5",
    },
    overage: {
      label: t("status.overage"),
      ring: "border-red-500/50",
      dot: "bg-red-400",
      text: "text-red-300",
      bg: "bg-red-500/10",
    },
  };
};

const MetricCard = ({ label, value, sub, accent, icon: Icon, testId }) => (
  <Card
    className={cn(
      "relative overflow-hidden bg-[#131620] border-[#272A35] rounded-xl p-5 md:p-6",
      "shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]",
    )}
    data-testid={testId}
  >
    <div className="flex items-start justify-between">
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-medium">
        {label}
      </div>
      {Icon && <Icon className="h-4 w-4 text-muted-foreground/60" strokeWidth={1.5} />}
    </div>
    <div
      className={cn(
        "font-mono-terminal font-bold mt-3 tracking-tighter",
        "text-4xl md:text-[42px] leading-none",
        accent,
      )}
    >
      {value}
    </div>
    {sub && (
      <div className="mt-2 text-xs text-slate-400 font-mono-terminal">
        {sub}
      </div>
    )}
  </Card>
);

export default function MetricsPanel({ result }) {
  const { t } = useT();
  const statusStyles = useStatusStyles();
  const s = statusStyles[result.status];
  const remainingAccent =
    result.status === "overage"
      ? "text-red-300"
      : result.status === "warning"
        ? "text-amber-300"
        : "text-emerald-300";

  return (
    <TooltipProvider delayDuration={100}>
      <section className="space-y-4" data-testid="metrics-panel">
      <div
        className={cn(
          "rounded-xl border px-4 py-3 flex items-center gap-3 flex-wrap",
          s.bg,
          s.ring,
        )}
        data-testid="status-banner"
      >
        <span className={cn("h-2 w-2 rounded-full shrink-0", s.dot)} />
        <span className={cn("font-medium text-sm", s.text)}>
          {t("status.label")} {s.label}
        </span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <MetricCard
          label={t("metric.used")}
          value={
            <>
              {result.daysUsed}
              <span className="text-slate-500 text-2xl"> / 90</span>
            </>
          }
          accent="text-white"
          icon={Clock}
          testId="metric-used"
        />
        <MetricCard
          label={t("metric.remaining")}
          value={result.remaining}
          sub={
            result.lastAllowedExit && result.status !== "overage" ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    className="inline-flex items-center gap-1.5 cursor-help"
                    data-testid="remaining-last-exit-hint"
                  >
                    <CalendarRange className="h-3 w-3 text-slate-500 shrink-0" />
                    <span>
                      {t("metric.mustExitBy")}{" "}
                      <span
                        className="text-slate-200 underline decoration-dotted underline-offset-2 decoration-slate-500"
                        data-testid="remaining-last-exit"
                      >
                        {formatDMY(result.lastAllowedExit)}
                      </span>
                    </span>
                    <HelpCircle className="h-3 w-3 text-slate-500" />
                  </span>
                </TooltipTrigger>
                <TooltipContent
                  side="bottom"
                  align="start"
                  className="bg-[#0C0E14] border border-[#272A35] text-slate-100 max-w-xs px-3 py-2 shadow-xl"
                >
                  <div className="text-[11px] uppercase tracking-[0.15em] text-slate-400 mb-1.5">
                    {t("metric.mustExitBy.explainTitle")}
                  </div>
                  <p className="text-xs text-slate-300 leading-relaxed">
                    {t("metric.mustExitBy.explain", {
                      date: formatDMY(result.lastAllowedExit),
                      windowStart: formatDMY(
                        addDays(result.lastAllowedExit, -179),
                      ),
                    })}
                  </p>
                  <p className="mt-1.5 text-[11px] text-slate-500 leading-relaxed">
                    {t("metric.mustExitBy.note")}
                  </p>
                </TooltipContent>
              </Tooltip>
            ) : null
          }
          accent={remainingAccent}
          icon={CheckCircle2}
          testId="metric-remaining"
        />
        <Card
          className={cn(
            "relative overflow-hidden rounded-xl p-5 md:p-6",
            "shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]",
            result.overage > 0
              ? "bg-red-500/10 border-red-500/40"
              : "bg-[#131620] border-[#272A35]",
          )}
          data-testid="metric-overage"
        >
          <div className="flex items-start justify-between">
            <div
              className={cn(
                "text-[10px] uppercase tracking-[0.18em] font-medium",
                result.overage > 0 ? "text-red-300" : "text-muted-foreground",
              )}
            >
              {t("metric.overage")}
            </div>
            <AlertTriangle
              className={cn(
                "h-4 w-4",
                result.overage > 0
                  ? "text-red-400"
                  : "text-muted-foreground/60",
              )}
              strokeWidth={1.5}
            />
          </div>          <div
            className={cn(
              "font-mono-terminal font-bold mt-3 tracking-tighter",
              "text-4xl md:text-[42px] leading-none",
              result.overage > 0 ? "text-red-300" : "text-slate-500",
            )}
          >
            {result.overage}
          </div>
          {result.overage > 0 && (
            <div
              className="mt-4 pt-4 border-t border-red-500/20 space-y-2"
              data-testid="overage-details"
            >
              {result.firstOverageDay && (
                <div className="flex items-start gap-2 text-xs">
                  <span className="text-red-400/70 mt-0.5 shrink-0">
                    <AlertTriangle className="h-3 w-3" />
                  </span>
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.15em] text-red-400/80 font-medium">
                      {t("metric.overageStart")}
                    </div>
                    <div
                      className="font-mono-terminal text-red-200 text-sm mt-0.5"
                      data-testid="overage-start-date"
                    >
                      {formatDMY(result.firstOverageDay)}
                    </div>
                  </div>
                </div>
              )}
              {result.canReenterAt && (
                <div className="flex items-start gap-2 text-xs">
                  <span className="text-emerald-400/70 mt-0.5 shrink-0">
                    <CheckCircle2 className="h-3 w-3" />
                  </span>
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.15em] text-emerald-400/80 font-medium">
                      {t("metric.reentry")}
                    </div>
                    <div
                      className="font-mono-terminal text-emerald-200 text-sm mt-0.5"
                      data-testid="reentry-date"
                    >
                      {formatDMY(result.canReenterAt)}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </Card>
        <MetricCard
          label={t("metric.window")}
          value={
            <span className="text-lg md:text-xl leading-tight block">
              {formatDMY(result.windowStart)}
              <span className="text-slate-500"> — </span>
              {formatDMY(result.windowEnd)}
            </span>
          }
          sub={t("metric.windowSub")}
          accent="text-slate-200"
          icon={CalendarRange}
          testId="metric-window"
        />
      </div>
    </section>
    </TooltipProvider>
  );
}
